/*
The MIT License (MIT)

Copyright (c) 2014 Krasimir Tsonev

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
*/

/*
  This utility is forked from Gifffer (https://github.com/krasimir/gifffer).

  Its purpose is to prevent GIFs from auto-playing, something which
  the Widget needs to be mindful of in order to be WCAG compliant.

  The utility will replace the <img> tag with a newly generated <div>
  that contains only the first frame (roughly) of your animated GIF.
  It creates a play button on top of it and when the element is clicked it
  returns the original image.
*/

const playSize = 60

export default function processGifsForAnimationControl(ref) {
  if (!ref) {
    return
  }

  const images = ref.querySelectorAll('[data-giffer]')

  for (let i = 0; i < images.length; ++i) {
    process(images[i])
  }
}

function createContainer(el, altText) {
  var con = document.createElement('button')
  var cls = el.getAttribute('class')
  var id = el.getAttribute('id')
  var playButtonStyles = [
    'width:' + playSize + 'px',
    'height:' + playSize + 'px',
    'border-radius:' + playSize / 2 + 'px',
    'background:rgba(0, 0, 0, 0.3)',
    'position:absolute',
    'top:50%',
    'left:50%',
    'margin:-' + playSize / 2 + 'px'
  ].join(';')
  var playButtonIconStyles = [
    'width: 0',
    'height: 0',
    'border-top: 14px solid transparent',
    'border-bottom: 14px solid transparent',
    'border-left: 14px solid rgba(0, 0, 0, 0.5)',
    'position: absolute',
    'left: 26px',
    'top: 16px'
  ].join(';')

  if (cls) {
    con.setAttribute('class', el.getAttribute('class'))
  }

  if (id) {
    con.setAttribute('id', el.getAttribute('id'))
  }

  con.setAttribute(
    'style',
    'position: relative; cursor: pointer; background: none; border: none; padding: 0;'
  )
  con.setAttribute('aria-label', `Click to play or pause GIF animation for ${altText}`)

  // creating play button
  var play = document.createElement('div')
  play.setAttribute('class', 'giffer-play-button')
  play.setAttribute('style', playButtonStyles)

  var triangle = document.createElement('div')
  triangle.setAttribute('style', playButtonIconStyles)
  play.appendChild(triangle)

  // dom placement
  con.appendChild(play)
  el.parentNode.replaceChild(con, el)
  return { c: con, p: play }
}

function calculatePercentageDim(el, w, h, wOrig, hOrig) {
  var parentDimW = el.parentNode.offsetWidth
  var ratio = wOrig / hOrig

  if (w.toString().indexOf('%') > 0) {
    w = parseInt(w.toString().replace('%', ''))
    w = (w / 100) * parentDimW
    h = w / ratio
  } else if (h.toString().indexOf('%') > 0) {
    h = parseInt(h.toString().replace('%', ''))
    h = (h / 100) * parentDimW
    w = h * ratio
  }

  return { w: w, h: h }
}

function process(el) {
  let durationTimeout = null
  let gif = null
  let playing = false

  const altText = el.getAttribute('data-giffer-alt')
  const c = document.createElement('canvas')
  const duration = el.getAttribute('data-giffer-duration')
  const url = el.getAttribute('data-giffer')

  el.style.display = 'block'

  // waiting for image load
  el.onload = function() {
    if (!(c.getContext && c.getContext('2d'))) {
      return
    }

    const w = el.width
    const h = el.height

    // creating the container
    const cc = createContainer(el, altText)
    const con = cc.c
    const play = cc.p
    const dims = calculatePercentageDim(con, w, h, el.width, el.height)

    // listening for image click
    con.addEventListener('click', function() {
      clearTimeout(durationTimeout)

      if (!playing) {
        playing = true
        gif = document.createElement('img')
        gif.setAttribute('style', 'width:100%;height:100%;')
        gif.setAttribute('data-uri', `${Math.floor(Math.random() * 100000) + 1}`)
        setTimeout(function() {
          gif.src = url
        }, 0)
        con.removeChild(play)
        con.removeChild(c)
        con.appendChild(gif)
        if (parseInt(duration) > 0) {
          durationTimeout = setTimeout(function() {
            playing = false
            con.appendChild(play)
            con.removeChild(gif)
            con.appendChild(c)
            gif = null
          }, duration)
        }
      } else {
        playing = false
        con.appendChild(play)
        con.removeChild(gif)
        con.appendChild(c)
        gif = null
      }
    })

    // canvas
    c.width = dims.w
    c.height = dims.h
    c.getContext('2d').drawImage(el, 0, 0, dims.w, dims.h)
    con.appendChild(c)

    // setting the actual image size
    con.setAttribute(
      'style',
      'position:relative;cursor:pointer;width:' +
        dims.w +
        'px;height:' +
        dims.h +
        'px;background:none;border:none;padding:0;'
    )

    c.style.width = '100%'
    c.style.height = '100%'

    if (w.toString().indexOf('%') > 0 && h.toString().indexOf('%') > 0) {
      con.style.width = w
      con.style.height = h
    } else if (w.toString().indexOf('%') > 0) {
      con.style.width = w
      con.style.height = 'inherit'
    } else if (h.toString().indexOf('%') > 0) {
      con.style.width = 'inherit'
      con.style.height = h
    } else {
      con.style.width = dims.w + 'px'
      con.style.height = dims.h + 'px'
    }
  }
  el.src = url
}
